import { useState, useCallback, SetStateAction, Dispatch } from 'react';
import socketMessages from '@azzurri-group/types/dist/types/socketMessages';
import useAxios from 'axios-hooks';
import { useConfig } from 'common/providers/configProvider';
import { useOAT } from 'common/providers/oatProvider';
import { useContent } from 'content/content-provider';
import { tenDigitsRegex } from 'loyalty/utils/tenDigitsRegex';

interface UseIdValidationResponse {
  errorMessage: string | null;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  loading: boolean;
  handleSubmitMemberId: (
    e: React.FormEvent<HTMLFormElement> | null,
    qrResult?: string
  ) => Promise<void>;
}

const useIdValidation = (
  memberNumber: string,
  handleSubmit: () => void
): UseIdValidationResponse => {
  const [content] = useContent();
  const { socket } = useOAT();
  const { apiDomain } = useConfig();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [{ loading }, executeValidate] = useAxios<boolean>(
    {
      baseURL: apiDomain,
      method: 'GET',
    },
    { manual: true }
  );

  const submitMemberId = useCallback(
    async (memberId: string) => {
      const isValidFormat = tenDigitsRegex.test(memberId);

      if (!isValidFormat) {
        setErrorMessage(content.errorMessages.formatError);
        return;
      }

      try {
        const response = await executeValidate({
          url: `/v1/loyalty/${memberId}/validate`,
        });

        const isValidMemberId = response.data;

        if (!isValidMemberId) {
          setErrorMessage(content.errorMessages.validationError);
        } else {
          socket.emit(socketMessages.submitLoyaltyNumber, {
            memberNumber: memberId,
          });
          handleSubmit();
        }
      } catch (error) {
        setErrorMessage(content.errorMessages.serverError);
      }
    },
    [
      handleSubmit,
      content?.errorMessages?.formatError,
      content?.errorMessages?.serverError,
      content?.errorMessages?.validationError,
      executeValidate,
      socket,
    ]
  );

  const handleSubmitMemberId = useCallback(
    async (e: React.FormEvent<HTMLFormElement> | null, qrResult?: string) => {
      if (e) e.preventDefault();

      submitMemberId(qrResult || memberNumber);
    },
    [submitMemberId, memberNumber]
  );

  return {
    errorMessage,
    setErrorMessage,
    loading,
    handleSubmitMemberId,
  };
};

export default useIdValidation;
